import { useQuery } from "@apollo/client";
import { Autocomplete, TextField, Tooltip } from "@mui/material";
import { gql } from "generated-graphql";
import { useOmnisearch } from "providers/OmnisearchDataContext";
import { useCallback, useMemo } from "react";

import {
  ShowFilterId,
  SHOW_FILTER_ORDER,
  SHOW_FILTERS,
  applyShowFilterToSearch,
  detectActiveShowFilter,
  formatShowFilterLabel,
} from "./manifestShowFilters";
import { mergeDefaultAndOmnisearch } from "./mergeDefaultAndOmnisearch";

const PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY = gql(`
  query ProcessedManifestShowFilterCounts($search: String) {
    processedManifestShowFilterCounts(search: $search) {
      filterId
      count
    }
  }
`);

export interface ManifestShowFilterPickerProps {
  defaultSearch: string;
}

/**
 * Mutually exclusive Show filter dropdown (Year picker styling). Counts reflect
 * the page scope (tenant + year) only; choosing a filter updates omnisearch.
 */
export function ManifestShowFilterPicker({ defaultSearch }: ManifestShowFilterPickerProps) {
  const { omnisearch, setOmnisearchImmediate } = useOmnisearch();

  const { data, loading } = useQuery(PROCESSED_MANIFEST_SHOW_FILTER_COUNTS_QUERY, {
    variables: { search: defaultSearch },
    fetchPolicy: "cache-and-network",
  });

  const countByFilterId = useMemo(() => {
    const map = new Map<ShowFilterId, number>();
    for (const row of data?.processedManifestShowFilterCounts ?? []) {
      if (SHOW_FILTER_ORDER.includes(row.filterId as ShowFilterId)) {
        map.set(row.filterId as ShowFilterId, row.count);
      }
    }
    return map;
  }, [data]);

  const mergedSearch = useMemo(
    () => mergeDefaultAndOmnisearch(defaultSearch, omnisearch),
    [defaultSearch, omnisearch]
  );

  const activeFilterId = useMemo(() => detectActiveShowFilter(mergedSearch), [mergedSearch]);

  const options = SHOW_FILTER_ORDER;

  const getOptionLabel = useCallback(
    (option: ShowFilterId) => {
      const def = SHOW_FILTERS[option];
      const count = countByFilterId.get(option);
      if (loading && count == null) {
        return def.label;
      }
      return formatShowFilterLabel(def.label, count ?? 0);
    },
    [countByFilterId, loading]
  );

  const handleChange = useCallback(
    (_: unknown, value: ShowFilterId) => {
      const nextOmnisearch = applyShowFilterToSearch(omnisearch, value);
      setOmnisearchImmediate(nextOmnisearch);
    },
    [omnisearch, setOmnisearchImmediate]
  );

  return (
    <Autocomplete<ShowFilterId, false, true, false>
      options={options}
      value={activeFilterId}
      onChange={handleChange}
      getOptionLabel={getOptionLabel}
      renderOption={(props, option) => {
        const { key, ...liProps } = props;
        return (
          <Tooltip
            key={key}
            title={SHOW_FILTERS[option].description}
            placement="right"
            enterDelay={300}
          >
            <li {...liProps}>{getOptionLabel(option)}</li>
          </Tooltip>
        );
      }}
      renderInput={(params) => <TextField {...params} label="Show" variant="standard" />}
      disableClearable
      sx={{ minWidth: 280 }}
    />
  );
}
